/* eslint-disable functional/prefer-readonly-type */
import is from '@sindresorhus/is';
import { useCallback, useContext, useMemo } from 'react';

import { ApplicationContext } from '../../../../shared/source/context/context';
import { hardSoftSkillProficiencyWithUnknownLevelSchema } from '../../../../shared/source/data-contract/skill';

import type { SkillProficiencyLevelUpdate } from './SkillsGroupEdit';
import type {
    HardSkill,
    HardSoftSkill,
    HardSoftSkillProficiencyWithUnknownLevel,
    LanguageSkill,
    Skill,
    SkillShort,
    SoftSkill,
} from '../../../../shared/source/data-contract/skill';

type ProficiencySkillGroup<T extends HardSoftSkill> = readonly [HardSoftSkillProficiencyWithUnknownLevel, readonly T[]];

export type GroupedSkillsByProficiency<T extends HardSoftSkill> = readonly ProficiencySkillGroup<T>[];

type GroupSkillsByProficiencyReturn<T extends HardSoftSkill> = {
    readonly groupedSkillsByProficiency: GroupedSkillsByProficiency<T> | undefined;
};

export const isHardSoftSkill = (skill: Skill): skill is HardSoftSkill => ['hard', 'soft'].includes(skill.skillType);

export const isLanguageSkill = (skill: Skill): skill is LanguageSkill => skill.skillType === 'language';

type SkillProficiencyLevelName = {
    readonly getProficiencyLevelName: (
        skill: Pick<Skill, 'level' | 'skillType'>,
        hideUnknownProficiency?: boolean,
    ) => string;
};

export const useSkillProficiencyLevelName = (): SkillProficiencyLevelName => {
    const { translate } = useContext(ApplicationContext);

    const getProficiencyLevelName = useCallback(
        (skill: Skill, hideUnknownProficiency = false): string => {
            const { level } = skill;
            const isSkillLevelUnknown = level === 0;

            if (is.null(level) || (hideUnknownProficiency && isSkillLevelUnknown)) {
                return '';
            }

            if (isLanguageSkill(skill) && !is.nullOrUndefined(skill.level)) {
                return translate(`dialogs.skills.proficiency.language.${skill.level}`);
            }

            if (isHardSoftSkill(skill) && !is.nullOrUndefined(skill.level)) {
                return translate(`dialogs.skills.proficiency.skills.${skill.level}`);
            }

            return '';
        },
        [translate],
    );

    return { getProficiencyLevelName };
};

export const groupSkillsByProficiency = <T extends HardSoftSkill>(
    skills?: readonly T[],
): GroupedSkillsByProficiency<T> => {
    if (is.undefined(skills) || is.emptyArray(skills)) {
        return [];
    }

    const filteredSkills = skills.filter(isHardSoftSkill);
    const groupedSkills = new Map<HardSoftSkillProficiencyWithUnknownLevel, readonly T[]>();

    filteredSkills.forEach((skill) => {
        const skillLevel = skill.level ?? 0;
        const current = groupedSkills.get(skillLevel) ?? [];

        groupedSkills.set(skillLevel, [...current, { ...skill, level: skillLevel }]);
    });

    return [...groupedSkills].sort(([skillLevelA], [skillLevelB]) => skillLevelB - skillLevelA);
};

export const useGroupSkillsByProficiency = <T extends HardSoftSkill>(
    skills?: readonly T[],
): GroupSkillsByProficiencyReturn<T> => {
    const groupedSkillsByProficiency = useMemo(() => groupSkillsByProficiency(skills), [skills]);

    return { groupedSkillsByProficiency };
};

type HardSoftAndLanguageSplit = {
    readonly hardSoftSkills: HardSoftSkill[];
    readonly languageSkills: LanguageSkill[];
};

export const splitHardSoftAndLanguageSkills = (skills: readonly SkillShort[]): HardSoftAndLanguageSplit =>
    skills.reduce<HardSoftAndLanguageSplit>(
        (acc, skill) => {
            const { hardSoftSkills, languageSkills } = acc;
            if (isLanguageSkill(skill)) {
                languageSkills.push(skill);
            }
            if (isHardSoftSkill(skill)) {
                hardSoftSkills.push(skill);
            }

            return acc;
        },
        { hardSoftSkills: [], languageSkills: [] },
    );

type HardAndSoftAndLanguageSplit = {
    readonly hardSkills: HardSkill[];
    readonly languageSkills: LanguageSkill[];
    readonly softSkills: SoftSkill[];
};

const isHardSkill = (skill: Pick<Skill, 'skillType'>): skill is HardSkill => skill.skillType === 'hard';

const isSoftSkill = (skill: Pick<Skill, 'skillType'>): skill is SoftSkill => skill.skillType === 'soft';

export const splitHardAndSoftAndLanguageSkills = (skills: readonly SkillShort[]): HardAndSoftAndLanguageSplit =>
    skills.reduce<HardAndSoftAndLanguageSplit>(
        (acc, skill) => {
            const { hardSkills, languageSkills, softSkills } = acc;

            if (isHardSkill(skill)) {
                hardSkills.push(skill);
            }

            if (isLanguageSkill(skill)) {
                languageSkills.push(skill);
            }

            if (isSoftSkill(skill)) {
                softSkills.push(skill);
            }

            return acc;
        },
        { hardSkills: [], languageSkills: [], softSkills: [] },
    );

type MapType = Map<HardSoftSkillProficiencyWithUnknownLevel, ReadonlyArray<HardSoftSkill>>;

type HardSoftSkillProficiencyGroups = ReadonlyMap<
    HardSoftSkillProficiencyWithUnknownLevel,
    ReadonlyArray<HardSoftSkill>
>;

export const createCompleteHardSoftSkillProficiencyGroups = (
    groupedSkills: HardSoftSkillProficiencyGroups,
): HardSoftSkillProficiencyGroups =>
    [...hardSoftSkillProficiencyWithUnknownLevelSchema.options]
        .sort((a, b) => b.value - a.value)
        .reduce<MapType>((proficiencyGroup, proficiencyLevel) => {
            proficiencyGroup.set(proficiencyLevel.value, groupedSkills.get(proficiencyLevel.value) ?? []);

            return proficiencyGroup;
        }, new Map());

export const createUpdatedHardSoftSkillGroups = (
    skillUpdateData: SkillProficiencyLevelUpdate,
    skillGroups: HardSoftSkillProficiencyGroups,
): HardSoftSkillProficiencyGroups => {
    const { index, prevProficiencyLevel, proficiencyLevel, skillId } = skillUpdateData;

    const localSkillGroup = new Map(structuredClone(skillGroups));
    const prevSkillGroup = [...(localSkillGroup.get(prevProficiencyLevel) ?? [])];
    const targetedSkillIndex = prevSkillGroup.findIndex(({ id }) => id === skillId);

    if (targetedSkillIndex === -1) {
        return localSkillGroup;
    }

    const [targetSkill] = prevSkillGroup.splice(targetedSkillIndex, 1);
    localSkillGroup.set(prevProficiencyLevel, prevSkillGroup);

    const newSkillGroup = [...(localSkillGroup.get(proficiencyLevel) ?? [])];
    newSkillGroup.splice(index, 0, { ...targetSkill!, level: proficiencyLevel });
    localSkillGroup.set(proficiencyLevel, newSkillGroup);

    return localSkillGroup;
};

export const removeSkillFromSkillGroupsById = (
    skillId: string,
    skillGroups: HardSoftSkillProficiencyGroups,
): HardSoftSkillProficiencyGroups => {
    const updatedSkillGroups = [...skillGroups].map(
        ([level, skills]) => [level, skills.filter(({ id }) => id !== skillId)] as const,
    );

    return new Map(updatedSkillGroups);
};
